/* ============================================
   BeeLive Studio — beelive-configurateur.js  (v2 — data-driven)
   Génération pilotée par les métachamps produit (window.BEE_CONFIG) :
     BEE_CONFIG[code] = { cats:[...]|"...", mult:bool, profils:[...] }
   Les règles spéciales (séries, tableaux, autocollants, kits) restent
   dans window.BEE_GENERATION_CONFIG.
   ============================================ */
(function () {
  'use strict';

  // ════════════════════════════════════════════
  // Helpers
  // ════════════════════════════════════════════
  function formatPrice(price) {
    return parseFloat(price).toFixed(2).replace('.', ',');
  }

  function _icon(item, size) {
    if (!item) return '';
    const ico = item.ico || item.icon || '';
    if (typeof ico === 'string' && ico.indexOf('<svg') !== -1) return ico; // SVG brut → tel quel (coloré par le CSS .--svg svg)
    if (window.beeIconAuto) return window.beeIconAuto(ico, size || 20);
    return ico;
  }

  function _stickerLabel(ref, handle) {
    const isTaches = (ref || '').startsWith('PPVEL') || (handle || '').startsWith('tableau-taches-communes');
    return isTaches ? 'Sticker polymère brillant' : 'Sticker polymère mat';
  }
  function _pvcLabel(ref, handle) {
    const isTaches = (ref || '').startsWith('PPVEL') || (handle || '').startsWith('tableau-taches-communes');
    return isTaches ? 'Plaque PVC & adhésif brillant' : 'Plaque PVC & adhésif mat';
  }

  // ════════════════════════════════════════════
  // Lecture du config
  // ════════════════════════════════════════════
  function getConfig() {
    return window.BEE_GENERATION_CONFIG || {};
  }
  function getProfileConfig(profilId) {
    return getConfig()[profilId] || null;
  }
  function getProfilesList() {
    const cfg = getConfig();
    return Object.keys(cfg)
      .filter(k => k !== 'globalExclude' && k !== 'familyOrder' && typeof cfg[k] === 'object' && cfg[k]?.pieces)
      .map(k => ({ id: k, label: cfg[k].label || k }));
  }

  // Tri d'une liste d'items : sections (kits → suggestions → portes → produits),
  // produits regroupés par famille, et à l'intérieur d'une famille les plaques
  // "TOUJOURS" sont placées après les plaques normales de cette même famille.
  function _family(code) { return String(code || '').split('-').slice(0, 2).join('-'); }
  function _itemRank(it) {
    if (it.isKit) return 0;
    if (it.isSuggestion) return 1;
    if (it.isDoor) return 2;
    return 3;
  }
  function sortItems(arr) {
    const familyOrder = getConfig().familyOrder || [];
    const famRank = code => { const i = familyOrder.indexOf(_family(code)); return i === -1 ? 999 : i; };
    return arr
      .map((it, i) => ({ it, i }))
      .sort((a, b) =>
        (_itemRank(a.it) - _itemRank(b.it)) ||
        (famRank(a.it.ref) - famRank(b.it.ref)) ||
        ((a.it.always ? 1 : 0) - (b.it.always ? 1 : 0)) ||
        (a.i - b.i))
      .map(x => x.it);
  }

  // ════════════════════════════════════════════
  // Moteur de génération (data-driven)
  // ════════════════════════════════════════════
  function evalCondition(cond, qtys) {
    if (!cond) return true;
    const v = qtys[cond.piece] || 0;
    if ('min' in cond && v < cond.min) return false;
    if ('max' in cond && v > cond.max) return false;
    return true;
  }

  // Collecte kits / tableaux / autocollants depuis le config (dernier match écrase la qty)
  function collectExtra(list, qtys) {
    const order = [], qmap = {};
    (list || []).forEach(it => {
      if (!evalCondition(it.if, qtys)) return;
      if (!(it.code in qmap)) order.push(it.code);
      qmap[it.code] = it.qty || 1;
    });
    return order.map(code => ({ code, qty: qmap[code] }));
  }

  // Contenu des kits (métachamp kit_items_codes)
  function getKitContents(code) {
    const raw = window.BEE_KIT_CONTENTS?.[code];
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    return String(raw).split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
  }

  // Normalise les catégories d'un produit (chaîne "a, b" OU tableau)
  function _cats(d) {
    if (Array.isArray(d.cats)) return d.cats.map(s => String(s).trim()).filter(Boolean);
    return String(d.cats || '').split(',').map(s => s.trim()).filter(Boolean);
  }

  // Les tags de profil sont écrits « Gite » sur les fiches importées et
  // « gite » sur certaines fiches créées à la main. On compare donc sans
  // tenir compte de la casse, quelle que soit la façon dont un tag sera
  // écrit demain.
  function _aProfil(d, tag) {
    const t = String(tag || '').toLowerCase();
    return ((d && d.profils) || []).some(p => String(p).toLowerCase() === t);
  }

  // Index { catégorie -> [{code, mult}] } pour un profil donné
  function _catIndexForProfile(tag) {
    const byCat = {};
    const cfg = window.BEE_CONFIG || {};
    for (const code in cfg) {
      const d = cfg[code];
      if (!_aProfil(d, tag)) continue;
      _cats(d).forEach(cat => { (byCat[cat] = byCat[cat] || []).push({ code, mult: !!d.mult }); });
    }
    return byCat;
  }

  // Construit la liste à partir des catégories + règles spéciales
  function buildList(profilId, qtys, extrasOn) {
    const config = getProfileConfig(profilId);
    const tag = config.tag || profilId;
    const globalExclude = new Set(getConfig().globalExclude || []);
    const byCat = _catIndexForProfile(tag);

    // Codes "gérés" par un menu déroulant de pièce → exclus du pull générique
    // de la catégorie (seul le type choisi sera ajouté).
    const managed = new Set();
    (config.pieces || []).forEach(pc => (pc.variants?.options || []).forEach(o => managed.add(o.code)));

    // Codes de la série numérotée → exclus du pull générique eux aussi. Les plaques
    // de porte portent la catégorie « Chambre » et se multiplient : sans cette
    // exclusion, elles sortent une première fois en quantité = nb de chambres par
    // le tirage de catégorie, puis une seconde fois à l'unité par la règle `series`.
    const seriesCodes = new Set();
    if (config.series && config.series.codeTemplate) {
      for (let i = 1; i <= (config.series.maxN || 0); i++) {
        seriesCodes.add(config.series.codeTemplate.replace('{n}', i));
      }
    }

    // 1) collecte category-driven avec dédup MAX (l'instance qui se multiplie l'emporte)
    //    sel = true si déclenché par une pièce/extra ; false si uniquement via TOUJOURS
    const picked = {}; // code -> { qty, src, sel }
    const consider = (code, qty, src, sel) => {
      if (globalExclude.has(code) || seriesCodes.has(code)) return;
      const p = picked[code] || { qty: 0, src: 0, sel: false };
      p.qty = Math.max(p.qty, qty);
      p.src = Math.max(p.src, src);
      if (sel) p.sel = true;
      picked[code] = p;
    };

    // TOUJOURS (qty 1, non sélectionné → ira en fin de liste)
    (byCat['TOUJOURS'] || []).forEach(p => consider(p.code, 1, 1, false));

    // pièces (compteur). On saute les codes gérés par menu déroulant.
    (config.pieces || []).forEach(pc => {
      const n = qtys[pc.id] || 0;
      if (n <= 0) return;
      (byCat[pc.id] || []).forEach(p => {
        if (managed.has(p.code)) return;
        consider(p.code, p.mult ? n : 1, n, true);
      });
      // types choisis via compteurs multi (qty = compteur propre à chaque type)
      if (pc.variants?.options?.length) {
        const vq = pieceVariantQtys[pc.id] || {};
        pc.variants.options.forEach(o => {
          const q = vq[o.code] || 0;
          if (q > 0 && !globalExclude.has(o.code)) consider(o.code, q, q, true);
        });
      }
    });

    // extras (compteur = 1)
    (extrasOn || []).forEach(id => {
      (byCat[id] || []).forEach(p => consider(p.code, 1, 1, true));
    });

    // 2) règles spéciales
    const doors = [];
    if (config.series && (qtys[config.series.trigger] || 0) > 0) {
      const s = config.series;
      const n = Math.min(qtys[s.trigger], s.maxN || 999);
      for (let i = 1; i <= n; i++) {
        const code = (s.codeTemplate || '').replace('{n}', i);
        if (globalExclude.has(code)) continue;
        doors.push({ ref: code, nom: (s.nameTemplate || '').replace('{n}', i), ico: s.icon, qty: 1, why: (s.nameTemplate || '').replace('{n}', i) });
      }
    }
    const kits         = collectExtra((config.kits || []).map(c => ({ code: c })), qtys).filter(k => !globalExclude.has(k.code));
    const tableaux     = collectExtra(config.tableaux, qtys).filter(t => !globalExclude.has(t.code));
    const autocollants = collectExtra(config.autocollants, qtys).filter(s => !globalExclude.has(s.code));

    // 3) détail (pré-maillage) — sert aussi de backup pour la restauration au retrait d'un kit
    const detailAll = Object.keys(picked).map(code => {
      const prod = fetchProductByRef(code);
      return { ref: code, qty: picked[code].qty, src: picked[code].src, sel: picked[code].sel, nom: prod?.title || code, ico: '✨', why: '' };
    });

    // 4) maillage kits : on retire du détail les codes couverts par un kit actif SI src < 2
    const inKits = new Set();
    kits.forEach(k => getKitContents(k.code).forEach(c => inKits.add(c)));
    const detail = detailAll.filter(it => (!inKits.has(it.ref) || it.src >= 2));

    // détail "normal" (déclenché par une pièce/extra) vs "TOUJOURS" (rejeté en fin)
    const normalDetail = detail.filter(it => it.sel);
    const alwaysDetail = detail.filter(it => !it.sel);

    const kitItems     = kits.map(k => ({ ref: k.code, qty: k.qty, ico: '📦', nom: fetchProductByRef(k.code)?.title || k.code, why: 'Pack tout-en-un', isKit: true }));
    const tableauItems = tableaux.filter(t => !inKits.has(t.code)).map(t => ({ ref: t.code, qty: t.qty, ico: '📋', nom: fetchProductByRef(t.code)?.title || t.code, why: 'Tableau de tâches', isSuggestion: true }));
    const stickerItems = autocollants.filter(s => !inKits.has(s.code)).map(s => ({ ref: s.code, qty: s.qty, ico: '✨', nom: fetchProductByRef(s.code)?.title || s.code, why: 'Rouleau', isSuggestion: true }));

    const mapDetail = d => ({ ref: d.ref, qty: d.qty, ico: d.ico, nom: d.nom, why: d.why });

    // sections en ordre, puis regroupement par famille à l'intérieur (sortItems)
    const combinedItems = sortItems([
      ...kitItems,
      ...tableauItems,
      ...stickerItems,
      ...doors.map(d => ({ ...d, isDoor: true })),
      ...normalDetail.map(mapDetail),
      ...alwaysDetail.map(d => ({ ...mapDetail(d), always: true })),
    ]);

    return { combinedItems, rawItemsBackup: detailAll, activeKitRefs: new Set(kits.map(k => k.code)) };
  }

  // Codes appartenant à un profil (pour suggestions + restauration kit)
  function _refsForProfile(profilId) {
    const refs = new Set();
    const config = getProfileConfig(profilId);
    const tag = config?.tag || profilId;
    const cfg = window.BEE_CONFIG || {};
    for (const code in cfg) {
      if (_aProfil(cfg[code], tag)) refs.add(code);
    }
    return refs;
  }

  let _exclusiveRefsCache = null;
  function getExclusiveRefs() {
    if (_exclusiveRefsCache) return _exclusiveRefsCache;
    const proOnly = new Set(), habitatOnly = new Set();
    const cfg = window.BEE_CONFIG || {};
    for (const code in cfg) {
              const hasPro = _aProfil(cfg[code], 'pro');
        const hasHabitat = ['gite', 'airbnb', 'coloc'].some(t => _aProfil(cfg[code], t));
      if (hasPro && !hasHabitat) proOnly.add(code);
      if (hasHabitat && !hasPro) habitatOnly.add(code);
    }
    _exclusiveRefsCache = { proOnly, habitatOnly };
    return _exclusiveRefsCache;
  }

  // ════════════════════════════════════════════
  // État
  // ════════════════════════════════════════════
  let profil = null;
  const qtys = {};
  const pieceVariantQtys = {}; // pièce.id -> { code interne: quantité } (compteurs multi-types)
  const extrasOn = new Set();
  let fmt = 'adhesif';
  let resultItems = [];
  let rawItemsBackup = [];
  let activeKitRefs = new Set();

  // ════════════════════════════════════════════
  // Cache produits
  // ════════════════════════════════════════════
  function fetchProductByRef(ref) {
    let d = window.BEE_PRODUCTS?.[ref];
    if (!d && window.BEE_BY_CODE?.[ref]) {
      d = window.BEE_BY_HANDLE?.[window.BEE_BY_CODE[ref]];
    }
    if (!d) return null;
    return { handle: d.h, title: d.t, featured_image: d.i, images: d.imgs || (d.i ? [d.i] : []), variants: d.v, description: JSON.parse(d.b || '""') };
  }

  function fetchAllParallel(refs) {
    return Promise.resolve(refs.map(ref => fetchProductByRef(ref)));
  }

  // ════════════════════════════════════════════
  // Mémoire de session : la génération survit à un aller-retour vers une fiche produit
  // ════════════════════════════════════════════
  const BEE_CFG_KEY = 'bee-config-state';

  function saveConfigState() {
    try {
      if (!profil || !resultItems.length) { sessionStorage.removeItem(BEE_CFG_KEY); return; }
      const items = resultItems.map(function (it, i) {
        const copy = Object.assign({}, it);
        delete copy.product;
        const card = document.querySelector('#bee-config-grid .bee-config-card[data-idx="' + i + '"]');
        const qtyEl = card && card.querySelector('.bee-config-card__qty-val');
        if (qtyEl) copy.qty = parseInt(qtyEl.textContent, 10) || copy.qty;
        return copy;
      });
      sessionStorage.setItem(BEE_CFG_KEY, JSON.stringify({
        t: Date.now(),
        profil: profil,
        qtys: qtys,
        pieceVariantQtys: pieceVariantQtys,
        extrasOn: Array.from(extrasOn),
        fmt: fmt,
        items: items,
        rawItemsBackup: rawItemsBackup,
        activeKitRefs: Array.from(activeKitRefs)
      }));
    } catch (e) {}
  }

  async function restoreConfigState() {
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(BEE_CFG_KEY) || 'null'); } catch (e) {}
    if (!saved || !saved.profil || !saved.items || !saved.items.length) return;

    // selectProfil() remet les valeurs par défaut : on l'appelle d'abord, on réinjecte ensuite.
    selectProfil(saved.profil);
    Object.keys(qtys).forEach(function (k) { delete qtys[k]; });
    Object.assign(qtys, saved.qtys || {});
    Object.keys(pieceVariantQtys).forEach(function (k) { delete pieceVariantQtys[k]; });
    Object.assign(pieceVariantQtys, saved.pieceVariantQtys || {});
    extrasOn.clear();
    (saved.extrasOn || []).forEach(function (x) { extrasOn.add(x); });
    fmt = saved.fmt || 'adhesif';
    document.querySelectorAll('.bee-config-fmt-btn').forEach(function (b) {
      b.classList.toggle('bee-config-fmt-btn--active', b.dataset.fmt === fmt);
    });
    renderPieces();
    renderExtras();

    if (!window.BEE_PRODUCTS_READY) {
      await new Promise(function (resolve) {
        document.addEventListener('bee:products:ready', resolve, { once: true });
      });
    }

    rawItemsBackup = saved.rawItemsBackup || [];
    activeKitRefs = new Set(saved.activeKitRefs || []);
    resultItems = saved.items.map(function (it) {
      return Object.assign({}, it, { product: fetchProductByRef(it.ref) });
    });

    renderResult();
    renderSummary();
    setupHelpBubble();
    applyFormatToAllCards();
    renderSuggestions();
    refreshConfigCardsCart();

    const res = document.getElementById('bee-config-result');
    if (res) res.classList.add('bee-config-result--visible');
    const genBtn = document.getElementById('bee-config-generate');
    if (genBtn) { genBtn.textContent = 'Regénérer →'; genBtn.disabled = false; }
  }

  // ════════════════════════════════════════════
  // Bouton flottant
  // ════════════════════════════════════════════
  function updateFloat() {
    const floatBtn = document.getElementById('bee-config-float');
    if (floatBtn) floatBtn.classList.toggle('show', resultItems.length > 0);
  }

  // ════════════════════════════════════════════
  // Sélection profil
  // ════════════════════════════════════════════
  function selectProfil(id) {
    profil = id;
    document.querySelectorAll('.bee-config-profil').forEach(p =>
      p.classList.toggle('bee-config-profil--active', p.dataset.id === id));

    const config = getProfileConfig(id);
    const pieces = config?.pieces || [];
    pieces.forEach(p => { qtys[p.id] = p.default; });
    Object.keys(pieceVariantQtys).forEach(k => delete pieceVariantQtys[k]);
    pieces.forEach(p => {
      if (p.variants?.options?.length) {
        pieceVariantQtys[p.id] = {};
        p.variants.options.forEach((o, i) => { pieceVariantQtys[p.id][o.code] = (i === 0 ? (p.default || 0) : 0); });
        qtys[p.id] = Object.values(pieceVariantQtys[p.id]).reduce((a, b) => a + b, 0);
      }
    });
    extrasOn.clear();

    renderPieces();
    renderExtras();

    document.getElementById('bee-config-bloc2')?.classList.add('bee-config-bloc--active');
    document.getElementById('bee-config-bloc3')?.classList.add('bee-config-bloc--active');
    document.getElementById('bee-config-generate')?.removeAttribute('disabled');
  }

  // ════════════════════════════════════════════
  // Rendu pièces
  // ════════════════════════════════════════════
  function renderPieces() {
    const grid = document.getElementById('bee-config-pieces');
    if (!grid) return;
    grid.innerHTML = '';
    if (!profil) {
      grid.innerHTML = '<p style="color:#A8B5BE;font-size:12px">Sélectionnez un profil ci-dessus</p>';
      return;
    }
    const config = getProfileConfig(profil);
    const pieces = config?.pieces || [];
    pieces.forEach(p => {
      const div = document.createElement('div');

      // ── Pièce à variantes : menu déroulant (chevron) ──
      if (p.variants?.options?.length) {
        const vq = pieceVariantQtys[p.id] || {};
        const sum = Object.values(vq).reduce((a, b) => a + b, 0);
        div.className = 'bee-config-piece bee-config-piece--variants' + (sum > 0 ? ' bee-config-piece--active' : '');
        div.style.flexDirection = 'column';
        div.style.alignItems = 'stretch';
        div.style.padding = '0';
        div.innerHTML = `
          <div class="bee-config-piece__vhead" data-piece="${p.id}" style="display:flex;align-items:center;gap:8px;cursor:pointer;padding:.625rem .875rem;user-select:none">
            <span class="bee-config-piece__ico bee-config-piece__ico--svg">${_icon(p, 24)}</span>
            <span class="bee-config-piece__label" style="flex:1">${p.label}</span>
            <span class="bee-config-piece__vcount" id="vcount-${p.id}" style="font-size:11px;font-weight:700;color:var(--or);background:#fff;border:1px solid var(--or-mid);border-radius:20px;padding:1px 9px;display:${sum > 0 ? 'inline-block' : 'none'}">${sum}</span>
            <svg class="bee-config-piece__chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:var(--txt2);transition:transform .2s;flex-shrink:0"><polyline points="6 9 12 15 18 9"></polyline></svg>
          </div>
          <div class="bee-config-piece__vpanel" style="display:none;flex-direction:column;padding:0 .875rem .25rem">
                          ${p.variants.options.map(o => {
                const on = (vq[o.code] || 0) > 0;
                return `
                <div class="bee-config-vrow${on ? ' bee-config-vrow--active' : ''}" style="display:flex;align-items:center;justify-content:space-between;gap:10px;padding:7px 8px;margin:2px 0;border-radius:6px">
                  <span class="bee-config-vrow__label" style="font-size:12px;line-height:1.25">${o.label}</span>
                  <div class="bee-config-qty">
                    <button class="bee-config-vqty__btn bee-config-qty__btn" data-piece="${p.id}" data-code="${o.code}" data-dir="-1">−</button>
                    <span class="bee-config-qty__val" id="vqty-${p.id}-${o.code}">${vq[o.code] || 0}</span>
                    <button class="bee-config-vqty__btn bee-config-qty__btn" data-piece="${p.id}" data-code="${o.code}" data-dir="1">+</button>
                  </div>
                </div>`;
              }).join('')}
          </div>
        `;
        grid.appendChild(div);
        return;
      }

      // ── Pièce standard : un seul compteur ──
      const q = qtys[p.id] ?? p.default;
      div.className = 'bee-config-piece' + (q > 0 ? ' bee-config-piece--active' : '');
      div.innerHTML = `
        <span class="bee-config-piece__ico bee-config-piece__ico--svg">${_icon(p, 24)}</span>
        <span class="bee-config-piece__label">${p.label}</span>
        <div class="bee-config-qty">
          <button class="bee-config-qty__btn" data-id="${p.id}" data-dir="-1">−</button>
          <span class="bee-config-qty__val" id="qty-${p.id}">${q}</span>
          <button class="bee-config-qty__btn" data-id="${p.id}" data-dir="1">+</button>
        </div>
      `;
      grid.appendChild(div);
    });
  }

  // ════════════════════════════════════════════
  // Rendu extras
  // ════════════════════════════════════════════
  function renderExtras() {
    const grid = document.getElementById('bee-config-extras');
    if (!grid) return;
    grid.innerHTML = '';
    if (!profil) {
      grid.innerHTML = '<p style="color:#A8B5BE;font-size:12px">Sélectionnez un profil ci-dessus</p>';
      return;
    }
    const config = getProfileConfig(profil);
    const extras = config?.extras || [];
    extras.forEach(e => {
      const btn = document.createElement('button');
      btn.className = 'bee-config-extra' + (extrasOn.has(e.id) ? ' bee-config-extra--active' : '');
      btn.dataset.id = e.id;
      btn.innerHTML = `<span class="bee-config-extra__ico bee-config-extra__ico--svg">${_icon(e, 18)}</span>${e.label}`;
      btn.addEventListener('click', () => {
        if (extrasOn.has(e.id)) extrasOn.delete(e.id);
        else extrasOn.add(e.id);
        btn.classList.toggle('bee-config-extra--active');
      });
      grid.appendChild(btn);
    });
  }

  // ════════════════════════════════════════════
  // Générer
  // ════════════════════════════════════════════
  async function generate() {
    if (!profil) return;
    const config = getProfileConfig(profil);
    if (!config) return;

    const genBtn = document.getElementById('bee-config-generate');
    genBtn.textContent = 'Chargement...';
    genBtn.disabled = true;

    if (!window.BEE_PRODUCTS_READY) {
      await new Promise(resolve => document.addEventListener('bee:products:ready', resolve, { once: true }));
    }

    const built = buildList(profil, qtys, extrasOn);
    const combinedItems = built.combinedItems;
    rawItemsBackup = built.rawItemsBackup;
    activeKitRefs = built.activeKitRefs;

    // Affichage immédiat
    resultItems = combinedItems.map(item => ({ ...item, product: null }));
    renderResult();
    renderSummary();
    setupHelpBubble();

    document.getElementById('bee-config-result')?.classList.add('bee-config-result--visible');
    document.getElementById('bee-config-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    genBtn.textContent = 'Regénérer →';
    genBtn.disabled = false;

    // Enrichissement en arrière-plan
    const refs = combinedItems.map(i => i.ref);
    fetchAllParallel(refs).then(products => {
      resultItems = combinedItems.map((item, i) => ({ ...item, product: products[i] }));

      resultItems.forEach((item, idx) => {
        if (!item.product) return;
        const card = document.querySelector(`.bee-config-card[data-idx="${idx}"]`);
        if (!card) return;
        const p = item.product;
        const variantA = p.variants[0];
        const variantP = p.variants[1] || p.variants[0];
        const hasTwo = p.variants.length > 1;
        const priceA = formatPrice(variantA.price);
        const priceP = formatPrice(variantP.price);

        /* ⚠️ Cette passe RÉÉCRIT l'image et le titre. Sans reposer les liens ici,
           ceux que renderResult() vient de créer sont détruits une demi-seconde
           après la génération : innerHTML efface la surface de clic de l'image,
           textContent remplace le <a> du titre par du texte brut.
           L'URL est recalculée à partir de BEE_BY_CODE, écrit par Liquid dans la
           page, donc toujours disponible. */
        const racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const h = window.BEE_BY_CODE?.[item.ref] || p.handle || window.BEE_PRODUCTS?.[item.ref]?.h || '';
        const url = h ? racine + 'products/' + h : '';

        const imgWrap = card.querySelector('.bee-config-card__img');
        if (imgWrap && p.featured_image) {
          const allImgs = (p.images && p.images.length ? p.images : [p.featured_image]).map(u => u.replace(/\.(jpg|png|gif|webp)/, '_800x.$1'));
          const hasMulti = allImgs.length > 1;
          imgWrap.innerHTML = `<img src="${allImgs[0]}" alt="${p.title}" loading="lazy">` + (hasMulti ? `
            <button class="bee-config-card__img-arrow bee-config-card__img-arrow--prev" type="button" aria-label="Image précédente"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg></button>
            <button class="bee-config-card__img-arrow bee-config-card__img-arrow--next" type="button" aria-label="Image suivante"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg></button>` : '');
          imgWrap.dataset.idx = idx;
          if (hasMulti) { imgWrap.dataset.images = allImgs.join('|'); imgWrap.dataset.imgIdx = '0'; }

          if (url) {
            const lienImg = document.createElement('a');
            lienImg.className = 'bee-config-card__img-link';
            lienImg.href = url;
            lienImg.tabIndex = -1;
            lienImg.setAttribute('aria-hidden', 'true');
            imgWrap.appendChild(lienImg);
          }
        }

        const nameEl = card.querySelector('.bee-config-card__name');
        if (nameEl) {
          if (url) {
            /* Construit en DOM et non en chaîne : un titre comme
               « Organisation & conduite » passerait mal en innerHTML. */
            nameEl.textContent = '';
            const lienNom = document.createElement('a');
            lienNom.className = 'bee-config-card__name-link';
            lienNom.href = url;
            lienNom.textContent = p.title;
            nameEl.appendChild(lienNom);
          } else {
            nameEl.textContent = p.title;
          }
        }

        const fmtsWrap = card.querySelector('.bee-config-card__formats');
        if (fmtsWrap) {
          fmtsWrap.className = `bee-config-card__formats bee-config-card__formats--${hasTwo ? '2' : '1'}`;
          fmtsWrap.innerHTML = `
            <div class="bee-config-card__fmt bee-config-card__fmt--active" data-format="a" data-variant="${variantA.id}" data-price="${variantA.price}">
              <span class="bee-config-card__fmt-name">${variantA.title}</span>
              <span class="bee-config-card__fmt-price">${priceA} €<span class="ttc"> TTC</span></span>
            </div>
            ${hasTwo ? `
            <div class="bee-config-card__fmt" data-format="b" data-variant="${variantP.id}" data-price="${variantP.price}">
              <span class="bee-config-card__fmt-name">${variantP.title}</span>
              <span class="bee-config-card__fmt-price">${priceP} €<span class="ttc"> TTC</span></span>
            </div>` : ''}
          `;
        }

        card.dataset.variantAdhesif = variantA.id;
        card.dataset.variantPvc = variantP.id;
      });

      applyFormatToAllCards();
      renderSuggestions();
      refreshConfigCardsCart();
      saveConfigState();
    });
  }

  // ════════════════════════════════════════════
  // Rendu cartes
  // ════════════════════════════════════════════
  function renderResult() {
    const grid = document.getElementById('bee-config-grid');
    if (!grid) return;
    grid.innerHTML = '';

    resultItems.forEach((item, idx) => {
      const card = document.createElement('div');
      card.className = 'bee-config-card';
      card.dataset.idx = idx;
      if (item.isKit) card.dataset.isKit = '1';
      if ((item.ref || '').startsWith('PPVEL-TTC')) card.dataset.isTableau = '1';
      const product = item.product;
      const variants = product?.variants || [];
      const variantA = variants[0];
      const variantP = variants[1] || variants[0];
      const hasTwo = variants.length > 1;
      const imgSrc = product?.featured_image || '';
      const priceA = variantA ? formatPrice(variantA.price) : '—';
      const priceP = variantP ? formatPrice(variantP.price) : '—';
      const nameA = variantA?.title || 'Adhésif seul';
      const nameP = variantP?.title || '+ Support PVC';
      const descA = nameA.includes('seul') || nameA.includes('Adhésif') ? _stickerLabel(item.ref, product?.handle) : 'Plaque PVC & adhésif mat';
      const descP = _pvcLabel(item.ref, product?.handle);

      /* Le handle vient D'ABORD de BEE_BY_CODE, posé par Liquid dans la page :
         il est disponible dès le premier rendu. BEE_PRODUCTS, lui, est rempli
         par un fetch asynchrone — s'y fier seul, c'est risquer une carte sans
         lien quand la liste est générée avant la fin du chargement. */
      const handle = window.BEE_BY_CODE?.[item.ref] || window.BEE_PRODUCTS?.[item.ref]?.h || product?.handle || '';
      const shortDesc = (handle && window.BEE_SHORT_DESC?.[handle]) || '';
      const cardUrl = handle
        ? ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'products/' + handle
        : '';

      card.innerHTML = `
        <button class="bee-config-card__delete" data-idx="${idx}" title="Retirer" aria-label="Retirer">${window.beeIcon ? window.beeIcon('trash', 16) : '🗑️'}</button>
        <div class="bee-config-card__img${((item.ref||'').startsWith('PPVEL-TTC') || (product?.handle||'').startsWith('tableau-taches-communes')) ? ' bee-config-card__img--zoom' : ''}" data-idx="${idx}"${(product?.images && product.images.length > 1) ? ` data-images="${product.images.map(u => u.replace(/\.(jpg|png|gif|webp)/, '_800x.$1')).join('|')}" data-img-idx="0"` : ''}>
          ${imgSrc
            ? `<img src="${imgSrc.replace(/\.(jpg|png|gif|webp)/, '_800x.$1')}" alt="${item.nom}" loading="lazy">`
            : `<div class="bee-config-card__img-inner bee-config-card__img-inner--svg">${_icon(item, 48)}</div>`}
          ${cardUrl ? `<a class="bee-config-card__img-link" href="${cardUrl}" tabindex="-1" aria-hidden="true"></a>` : ''}
          ${(product?.images && product.images.length > 1) ? `
            <button class="bee-config-card__img-arrow bee-config-card__img-arrow--prev" type="button" aria-label="Image précédente"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg></button>
            <button class="bee-config-card__img-arrow bee-config-card__img-arrow--next" type="button" aria-label="Image suivante"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg></button>
          ` : ''}
        </div>
        <div class="bee-config-card__body">
          <span class="bee-config-card__cart-badge"></span>
          <div class="bee-config-card__name">${cardUrl ? `<a href="${cardUrl}" class="bee-config-card__name-link">${product?.title || item.nom}</a>` : (product?.title || item.nom)}</div>
          ${shortDesc ? `<div class="bee-config-card__desc">${shortDesc}</div>` : ''}
          <span class="bee-config-card__qty-badge">× ${item.qty}</span>
        </div>
        <div class="bee-config-card__sep"></div>
        <div class="bee-config-card__formats bee-config-card__formats--${hasTwo ? '2' : '1'}">
          <div class="bee-config-card__fmt bee-config-card__fmt--active" data-format="a" data-variant="${variantA?.id || ''}" data-price="${variantA?.price ?? ''}">
            <span class="bee-config-card__fmt-name">${nameA}</span>
            <span class="bee-config-card__fmt-price">${priceA} €<span class="ttc"> TTC</span></span>
          </div>
          ${hasTwo ? `
          <div class="bee-config-card__fmt" data-format="b" data-variant="${variantP?.id || ''}" data-price="${variantP?.price ?? ''}">
            <span class="bee-config-card__fmt-name">${nameP}</span>
            <span class="bee-config-card__fmt-price">${priceP} €<span class="ttc"> TTC</span></span>
          </div>` : ''}
        </div>
        <div class="bee-config-card__add-row">
          <div class="bee-config-card__qty">
            <button class="bee-config-card__qty-btn" data-dir="-1">−</button>
            <span class="bee-config-card__qty-val">${item.qty}</span>
            <button class="bee-config-card__qty-btn" data-dir="1">+</button>
          </div>
          <button class="bee-config-card__add-btn" data-idx="${idx}">Ajouter au panier</button>
        </div>
      `;
      grid.appendChild(card);
    });

    document.getElementById('bee-config-result-count').textContent = resultItems.length;
    applyFormatToAllCards();
    updateFloat();
  }

  // Synchronise le badge "dans le panier" des cartes générées
  function syncConfigCardsWithCart(cart) {
    if (!cart) return;
    const inCart = {};
    (cart.items || []).forEach(it => { inCart[String(it.variant_id)] = it.quantity; });
    document.querySelectorAll('#bee-config-grid .bee-config-card').forEach(card => {
      let qty = 0;
      card.querySelectorAll('.bee-config-card__fmt').forEach(f => {
        const id = f.dataset.variant;
        if (id && inCart[id]) qty = Math.max(qty, inCart[id]);
      });
      const badge = card.querySelector('.bee-config-card__cart-badge');
      if (qty > 0) {
        card.classList.add('bee-config-card--in-cart');
        if (badge) badge.textContent = qty > 1 ? `× ${qty} dans le panier` : '✓ Dans le panier';
      } else {
        card.classList.remove('bee-config-card--in-cart');
        if (badge) badge.textContent = '';
      }
    });
  }
  function refreshConfigCardsCart() {
    fetch('/cart.js').then(r => r.json()).then(syncConfigCardsWithCart).catch(() => {});
  }

  // ── Applique le fmt courant à toutes les cartes ──
  function applyFormatToAllCards() {
    document.querySelectorAll('#bee-config-grid .bee-config-card').forEach(card => {
      const fmts = card.querySelectorAll('.bee-config-card__fmt');
      if (!fmts.length) return;
      const hasTwo = fmts.length > 1;
      const target = (fmt === 'pvc' && hasTwo) ? 'b' : 'a';
      fmts.forEach(f => {
        f.classList.toggle('bee-config-card__fmt--active', f.dataset.format === target);
      });
    });
    renderTotal();
  }

  // ════════════════════════════════════════════
  // Résumé
  // ════════════════════════════════════════════
  function renderSummary() {
    const config = getProfileConfig(profil);
    const profilEl = document.getElementById('bee-config-summary-profil');
    if (profilEl) profilEl.textContent = config?.label || '';
    // Ligne "détail" (pièces sélectionnées · format) retirée à la demande
    const detailEl = document.getElementById('bee-config-summary-detail');
    if (detailEl) detailEl.textContent = '';
    const countEl = document.getElementById('bee-config-summary-count');
    if (countEl) {
      const totalQty = resultItems.reduce((sum, item) => sum + item.qty, 0);
      countEl.textContent = totalQty;
    }
    renderTotal();
  }

  /* Total courant de la liste generee.
     Lu dans le DOM et non dans resultItems : la quantite et le format choisi
     vivent dans la carte, et c'est ce que le client a sous les yeux. Le prix
     est pose en euros dans data-price au rendu de la carte, converti en
     centimes ici pour que la somme ne derive pas en virgule flottante.
     Tant que les produits ne sont pas charges, data-price est vide : le total
     affiche un tiret au lieu d'un faux zero. */
  function renderTotal() {
    const totalEl = document.getElementById('bee-config-summary-total');
    if (!totalEl) return;
    let cents = 0;
    let connus = 0;
    document.querySelectorAll('#bee-config-grid .bee-config-card').forEach(card => {
      const fmt = card.querySelector('.bee-config-card__fmt--active')
               || card.querySelector('.bee-config-card__fmt');
      const prix = parseFloat(fmt?.dataset.price);
      const qte = parseInt(card.querySelector('.bee-config-card__qty-val')?.textContent, 10);
      if (!isNaN(prix) && !isNaN(qte)) { cents += Math.round(prix * 100) * qte; connus++; }
    });
    totalEl.textContent = connus ? formatPrice(cents / 100) + ' €' : '—';
  }

  // ════════════════════════════════════════════
  // Suggestions (Zone 2)
  // ════════════════════════════════════════════
  async function renderSuggestions() {
    const section = document.getElementById('bee-config-suggest');
    const grid = document.getElementById('bee-config-suggest-grid');
    if (!section || !grid) return;

    try {
      /* Sélection confiée au moteur commun (assets/bee-produits-lies.js) :
         mêmes règles que la section « BEE — Produits liés ». Sources = les
         produits de la liste générée ; profil = celui choisi par le visiteur. */
      if (!window.beeProduitsLies && document.readyState === 'loading') {
        await new Promise(r => document.addEventListener('DOMContentLoaded', r, { once: true }));
      }
      if (!window.beeProduitsLies) { section.style.display = 'none'; return; }

      const alreadyInList = resultItems.map(i => i.product?.handle).filter(Boolean);

      const { proOnly, habitatOnly } = getExclusiveRefs();
      const profileExclude = profil === 'pro' ? habitatOnly : proOnly;
      const excluHandles = [...profileExclude].map(code => window.BEE_BY_CODE?.[code]).filter(Boolean);

      const PROFILS_TAG = { gite: 'Gite', airbnb: 'Airbnb', coloc: 'Coloc', pro: 'Pro' };
      const res = await window.beeProduitsLies.calculer(alreadyInList, {
        limite: 15,
        profils: PROFILS_TAG[profil] ? [PROFILS_TAG[profil]] : [],
        exclure: excluHandles,
        sansSeries: true
      });

      const displayed = res.map(c => ({
        title: c.p.title,
        featured_image: c.p.images[0] || '',
        images: c.p.images,
        variants: c.p.variants,
        description: '""',
        handle: c.p.handle,
        tags: ''
      }));

      if (!displayed.length) { section.style.display = 'none'; return; }

      section.style.display = 'block';
      grid.innerHTML = '';

      displayed.forEach(product => {
        if (!product) return;
        const wrapper = document.createElement('div');
        wrapper.dataset.profil = 'all';
        wrapper.innerHTML = window.beeBuildPcard({
          title: product.title,
          img: product.featured_image || '',
          images: product.images || (product.featured_image ? [product.featured_image] : []),
          handle: product.handle,
          shortDesc: window.BEE_SHORT_DESC?.[product.handle] || '',
          variants: product.variants.map(v => ({ id: v.id, title: v.title, price: v.price }))
        });
        grid.appendChild(wrapper);
      });

      // Restaurer l'état panier
      fetch('/cart.js').then(r => r.json()).then(cart => {
        const cartItems = {};
        cart.items.forEach(item => { cartItems[String(item.variant_id)] = item.quantity; });
        grid.querySelectorAll('.bee-pcard').forEach(card => {
          const vA = card.dataset.variantAdhesif;
          const vP = card.dataset.variantPvc;
          let qty = 0;
          if (vA && cartItems[vA]) qty = cartItems[vA];
          else if (vP && cartItems[vP]) qty = cartItems[vP];
          if (qty > 0) {
            card.classList.add('bee-pcard--in-cart');
            const badge = card.querySelector('.bee-pcard__cart-badge');
            if (badge) { badge.dataset.qty = qty; badge.textContent = qty > 1 ? `× ${qty} dans le panier` : '✓ Dans le panier'; }
            const btn = card.querySelector('.bee-pcard__add-btn');
            if (btn) btn.style.background = '#C7801D';
          }
        });
      });

    } catch (e) {
      console.warn('Suggestions error:', e);
      section.style.display = 'none';
    }
  }

  // ════════════════════════════════════════════
  // ATC carte
  // ════════════════════════════════════════════
  async function addCardToCart(btn) {
    const card = btn.closest('.bee-config-card');
    const variantId = parseInt(card.querySelector('.bee-config-card__fmt--active')?.dataset.variant);
    const qty = parseInt(card.querySelector('.bee-config-card__qty-val').textContent) || 1;
    if (!variantId) return;
    btn.disabled = true; btn.textContent = '...';
    try {
      await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: variantId, quantity: qty }) });
      const cart = await fetch('/cart.js').then(r => r.json());
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: cart } }));
      btn.textContent = '✓ Ajouté !'; btn.classList.add('ok');
      card.classList.add('bee-config-card--in-cart');
      setTimeout(() => { btn.textContent = 'Ajouter au panier'; btn.classList.remove('ok'); btn.disabled = false; }, 2000);
    } catch { btn.textContent = 'Erreur'; btn.disabled = false; }
  }

  // ════════════════════════════════════════════
  // Tout ajouter au panier
  // ════════════════════════════════════════════
  async function addAllToCart() {
    const btn = document.getElementById('bee-config-atc-all');
    if (!btn) return;
    btn.disabled = true;

    const cards = Array.from(document.querySelectorAll('#bee-config-grid .bee-config-card'));
    const items = [];
    cards.forEach(card => {
      const variantId = parseInt(card.querySelector('.bee-config-card__fmt--active')?.dataset.variant);
      const qty = parseInt(card.querySelector('.bee-config-card__qty-val')?.textContent) || 1;
      if (variantId) items.push({ id: variantId, quantity: qty });
    });
    if (!items.length) { btn.disabled = false; return; }

    const overlay = document.createElement('div');
    overlay.id = 'bee-atc-overlay';
    overlay.innerHTML = `
      <div class="bee-atc-overlay__card">
        <div class="bee-atc-overlay__title">Ajout au panier en cours</div>
        <div class="bee-atc-overlay__subtitle">Merci de patienter, ne quittez pas la page…</div>
        <div class="bee-atc-overlay__bar">
          <div class="bee-atc-overlay__bar-fill" id="bee-atc-bar-fill"></div>
        </div>
        <div class="bee-atc-overlay__count" id="bee-atc-count">${items.length} article${items.length > 1 ? 's' : ''}</div>
      </div>
    `;
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => overlay.classList.add('show'));

    const barFill = document.getElementById('bee-atc-bar-fill');
    const countEl = document.getElementById('bee-atc-count');

    if (barFill) {
      barFill.style.transition = 'width 4s ease-out';
      requestAnimationFrame(() => barFill.style.width = '90%');
    }

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const cart = await fetch('/cart.js').then(r => r.json());
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: cart } }));
      document.querySelectorAll('#bee-config-grid .bee-config-card').forEach(c => c.classList.add('bee-config-card--in-cart'));

      if (barFill) { barFill.style.transition = 'width .3s ease'; barFill.style.width = '100%'; }
      if (countEl) countEl.textContent = '✓ Terminé !';
      await new Promise(r => setTimeout(r, 700));
    } catch (e) {
      if (countEl) countEl.textContent = '⚠️ Erreur — Réessayez';
      await new Promise(r => setTimeout(r, 1500));
    }

    overlay.classList.remove('show');
    setTimeout(() => {
      overlay.remove();
      document.body.style.overflow = '';
    }, 300);

    btn.textContent = 'Tout ajouté !';
    btn.classList.add('ok');
    setTimeout(() => { btn.textContent = 'Tout ajouter au panier'; btn.classList.remove('ok'); btn.disabled = false; }, 3000);
  }

  // ── Bulle d'aide "Tout ajouter au panier" ──
  function setupHelpBubble() {
    const atcBtn = document.getElementById('bee-config-atc-all');
    if (!atcBtn) return;
    if (document.getElementById('bee-config-help-bubble')) return;
    const dismissedAt = sessionStorage.getItem('bee-config-help-dismissed-at');
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < 3 * 60 * 1000) return;

    const container = atcBtn.parentElement;
    if (!container) return;
    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }

    const bubble = document.createElement('div');
    bubble.id = 'bee-config-help-bubble';
    bubble.innerHTML = `
      <span class="bee-help-bubble__icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.8 1.3 1.5 1.5 2.5"></path><path d="M9 18h6"></path><path d="M10 22h4"></path></svg></span>
      <div class="bee-help-bubble__text">
        <strong>Astuce</strong>
        Modifiez votre liste puis ajoutez tout au panier en 1 clic
      </div>
      <button class="bee-help-bubble__close" aria-label="Fermer">✕</button>
    `;
    container.appendChild(bubble);

    setTimeout(() => bubble.classList.add('show'), 100);

    bubble.querySelector('.bee-help-bubble__close').addEventListener('click', e => {
      e.stopPropagation();
      bubble.classList.remove('show');
      sessionStorage.setItem('bee-config-help-dismissed-at', Date.now().toString());
      setTimeout(() => bubble.remove(), 300);
    });
  }

  // ── Flèches image sur les cartes générées (swap sans ouvrir le lightbox) ──
  document.addEventListener('click', function (e) {
    const arrow = e.target.closest('.bee-config-card__img-arrow');
    if (!arrow) return;
    e.preventDefault();
    e.stopPropagation();
    const wrap = arrow.closest('.bee-config-card__img');
    if (!wrap || !wrap.dataset.images) return;
    const imgs = wrap.dataset.images.split('|');
    let idx = parseInt(wrap.dataset.imgIdx || '0');
    idx = arrow.classList.contains('bee-config-card__img-arrow--next')
      ? (idx + 1) % imgs.length
      : (idx - 1 + imgs.length) % imgs.length;
    wrap.dataset.imgIdx = idx;
    const img = wrap.querySelector('img');
    if (img) { img.removeAttribute('srcset'); img.src = imgs[idx]; }
  }, true);

  // ════════════════════════════════════════════
  // Events
  // ════════════════════════════════════════════
  document.addEventListener('click', function (e) {

    // Fermer les menus déroulants (toilettes) ouverts si le clic est en dehors
    document.querySelectorAll('.bee-config-piece--variants.is-open').forEach(pc => {
      if (!pc.contains(e.target)) {
        pc.classList.remove('is-open');
        const panel = pc.querySelector('.bee-config-piece__vpanel');
        const chev = pc.querySelector('.bee-config-piece__chevron');
        if (panel) panel.style.display = 'none';
        if (chev) chev.style.transform = '';
      }
    });

    // Ouvrir/fermer le menu déroulant d'une pièce à variantes
    const vHead = e.target.closest('.bee-config-piece__vhead');
    if (vHead) {
      const pc = vHead.closest('.bee-config-piece--variants');
      const open = pc.classList.toggle('is-open');
      const panel = pc.querySelector('.bee-config-piece__vpanel');
      const chev = pc.querySelector('.bee-config-piece__chevron');
      if (panel) panel.style.display = open ? 'flex' : 'none';
      if (chev) chev.style.transform = open ? 'rotate(180deg)' : '';
      return;
    }

    const profilBtn = e.target.closest('.bee-config-profil');
    if (profilBtn) { selectProfil(profilBtn.dataset.id); return; }

    const vqBtn = e.target.closest('.bee-config-vqty__btn');
    if (vqBtn) {
      const pid = vqBtn.dataset.piece;
      const code = vqBtn.dataset.code;
      const dir = parseInt(vqBtn.dataset.dir);
      const piece = (getProfileConfig(profil)?.pieces || []).find(p => p.id === pid);
      const max = piece?.max || 99;
      pieceVariantQtys[pid] = pieceVariantQtys[pid] || {};
      const next = Math.max(0, Math.min(max, (pieceVariantQtys[pid][code] || 0) + dir));
      pieceVariantQtys[pid][code] = next;
              const valEl = document.getElementById(`vqty-${pid}-${code}`);
        if (valEl) valEl.textContent = next;
        vqBtn.closest('.bee-config-vrow')?.classList.toggle('bee-config-vrow--active', next > 0);
      const sum = Object.values(pieceVariantQtys[pid]).reduce((a, b) => a + b, 0);
      qtys[pid] = sum;
      vqBtn.closest('.bee-config-piece')?.classList.toggle('bee-config-piece--active', sum > 0);
      const badge = document.getElementById(`vcount-${pid}`);
      if (badge) { badge.textContent = sum; badge.style.display = sum > 0 ? 'inline-block' : 'none'; }
      return;
    }

    const qBtn = e.target.closest('.bee-config-qty__btn[data-id]');
    if (qBtn) {
      const id = qBtn.dataset.id;
      const config = getProfileConfig(profil);
      const pieces = config?.pieces || [];
      const piece = pieces.find(p => p.id === id);
      if (!piece) return;
      qtys[id] = Math.max(0, Math.min(piece.max, (qtys[id] || 0) + parseInt(qBtn.dataset.dir)));
      const valEl = document.getElementById(`qty-${id}`);
      if (valEl) valEl.textContent = qtys[id];
      qBtn.closest('.bee-config-piece')?.classList.toggle('bee-config-piece--active', qtys[id] > 0);
      return;
    }

    const fmtBtn = e.target.closest('.bee-config-fmt-btn');
    if (fmtBtn?.dataset.fmt) {
      fmt = fmtBtn.dataset.fmt;
      document.querySelectorAll('.bee-config-fmt-btn').forEach(b => b.classList.toggle('bee-config-fmt-btn--active', b.dataset.fmt === fmt));
      applyFormatToAllCards();
      return;
    }

    if (e.target.closest('#bee-config-generate')) { generate(); return; }

    const delBtn = e.target.closest('.bee-config-card__delete');
    if (delBtn) {
      const idx = parseInt(delBtn.dataset.idx);
      const item = resultItems[idx];

      if (item?.isKit) {
        activeKitRefs.delete(item.ref);

        const stillInOtherKits = new Set();
        activeKitRefs.forEach(kr => getKitContents(kr).forEach(c => stillInOtherKits.add(c)));

        const currentRefs = new Set(resultItems.map(i => i.ref));
        const deletedKitContents = getKitContents(item.ref);

        deletedKitContents.forEach(code => {
          if (stillInOtherKits.has(code)) return;
          if (currentRefs.has(code)) return;

          const fromBackup = rawItemsBackup.find(it => it.ref === code);
          if (fromBackup) {
            resultItems.push({ ...fromBackup, product: fetchProductByRef(code) });
            return;
          }

          /* Pas de filtre sur les regles du profil ici. _refsForProfile() rend
             TOUT produit portant le tag du profil, pieces cochees ou non : s'en
             servir ecartait la quasi-totalite du contenu du kit, alors que rien
             ne le reajoute ensuite, resultItems n'etant pas regenere. Le doublon
             est deja couvert par currentRefs plus haut. */
          const product = fetchProductByRef(code);
          if (product) {
            resultItems.push({
              ref: code,
              nom: product.title,
              ico: '📦',
              qty: 1,
              why: 'Du kit retiré',
              product
            });
          }
        });

        resultItems = resultItems.filter((_, i) => i !== idx);
      } else {
        resultItems.splice(idx, 1);
      }

      resultItems = sortItems(resultItems); // regroupe les produits ré-ajoutés par famille
      renderResult();
      renderSummary();
      saveConfigState();
      return;
    }

    const cardFmt = e.target.closest('.bee-config-card__fmt');
    if (cardFmt) {
      cardFmt.closest('.bee-config-card').querySelectorAll('.bee-config-card__fmt').forEach(f => f.classList.remove('bee-config-card__fmt--active'));
      cardFmt.classList.add('bee-config-card__fmt--active');
      renderTotal();
      return;
    }

    const cqBtn = e.target.closest('.bee-config-card__qty-btn');
    if (cqBtn) {
      const val = cqBtn.closest('.bee-config-card__qty').querySelector('.bee-config-card__qty-val');
      const newQty = Math.max(1, Math.min(99, parseInt(val.textContent) + parseInt(cqBtn.dataset.dir)));
      val.textContent = newQty;
      const card = cqBtn.closest('.bee-config-card');
      const badge = card?.querySelector('.bee-config-card__qty-badge');
      if (badge) badge.textContent = `× ${newQty}`;
      let total = 0;
      document.querySelectorAll('#bee-config-grid .bee-config-card__qty-val').forEach(v => { total += parseInt(v.textContent) || 0; });
      document.getElementById('bee-config-summary-count').textContent = total;
      renderTotal();
      saveConfigState();
      return;
    }

    const addBtn = e.target.closest('#bee-config-grid .bee-config-card__add-btn');
    if (addBtn) { addCardToCart(addBtn); return; }

    /* Le clic sur l'image est géré par le lien .bee-config-card__img-link
       rendu dans la carte. Rien à intercepter ici. */
    if (e.target.closest('#bee-config-atc-all')) { addAllToCart(); return; }
    if (e.target.closest('#bee-config-float')) { document.getElementById('bee-config-result')?.scrollIntoView({ behavior: 'smooth' }); return; }

    const suggestArrow = e.target.closest('#bee-config-suggest-prev, #bee-config-suggest-next');
    if (suggestArrow) {
      const scrollEl = document.getElementById('bee-config-suggest-grid');
      if (!scrollEl) return;
      const wrapper = scrollEl.querySelector('[data-profil]');
      const wrapperWidth = wrapper ? wrapper.offsetWidth + 14 : 264;
      const isPrev = suggestArrow.id === 'bee-config-suggest-prev';
      if (isPrev) {
        if (scrollEl.scrollLeft <= 5) scrollEl.scrollLeft = scrollEl.scrollWidth;
        else scrollEl.scrollLeft -= wrapperWidth * 2;
      } else {
        if (scrollEl.scrollLeft >= scrollEl.scrollWidth - scrollEl.clientWidth - 5) scrollEl.scrollLeft = 0;
        else scrollEl.scrollLeft += wrapperWidth * 2;
      }
      return;
    }

    if (e.target.closest('#bee-config-reset')) {
      profil = null; resultItems = [];
      Object.keys(qtys).forEach(k => delete qtys[k]);
      extrasOn.clear(); fmt = 'adhesif';
      document.querySelectorAll('.bee-config-profil').forEach(p => p.classList.remove('bee-config-profil--active'));
      document.querySelectorAll('.bee-config-fmt-btn').forEach(b => b.classList.toggle('bee-config-fmt-btn--active', b.dataset.fmt === 'adhesif'));
      renderPieces(); renderExtras();
      document.getElementById('bee-config-result')?.classList.remove('bee-config-result--visible');
      document.getElementById('bee-config-suggest')?.style?.setProperty('display', 'none');
      document.getElementById('bee-config-float')?.classList.remove('show');
      saveConfigState();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

  });

  document.addEventListener('cart:update', function (e) {
    const cart = e.detail?.resource || e.detail?.data;
    if (cart) syncConfigCardsWithCart(cart);
    else refreshConfigCardsCart();
  });

  // Retour depuis une fiche produit (cache navigateur) : la page doit
  // redevenir défilable si le retour a eu lieu pendant l'ajout au panier.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.getElementById('bee-atc-overlay')?.remove();
    document.body.style.overflow = '';
  });

  // ════════════════════════════════════════════
  // Init
  // ════════════════════════════════════════════
  renderPieces();
  renderExtras();
  restoreConfigState();

})();